import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import feraWordmark from "../assets/brand/logos/fera-wordmark-transparent.png";
import siteAccess from "../siteAccess";
import { getSiteMode } from "../utils/siteMode";

const PRODUCT_NAV_ITEMS = [
  { label: "Platform Overview", route: "/platform" },
  { label: "Basecalling", route: "/product/basecalling" },
  { label: "Clustering", route: "/product/clustering" },
  { label: "Modification Discovery", route: "/product/modification-discovery" },
  { label: "Supplemental Tools", route: "/product/tools" },
];

const RESOURCES_NAV_ITEMS = [
  { label: "Overview", route: "/resources" },
  { label: "Guides", route: "/resources#guides" },
  { label: "Quickstart", route: "/resources#quickstart" },
  { label: "FAQ", route: "/resources#faq" },
  { label: "Contact Support", route: "/resources#support" },
];

const MAIN_NAV_ITEMS = [
  { label: "Home", route: "/" },
  { label: "Use Cases", route: "/use-cases" },
  { label: "About", route: "/about" },
];
const LOGIN_URL = "https://fera.cloud";

// Returns true if a nav item with the given label should be visible.
// Mirrors filterItemsByRole() from ferabio/src/getNavItems.js
const isNavVisible = (label, mode) => {
  const access = siteAccess[mode] || siteAccess.public;
  if (access === "all") return true;
  return !access.includes(label);
};

const LandingHeader = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = useState(false);
  const [mobileProductOpen, setMobileProductOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const location = useLocation();
  const navRef = useRef(null);
  const siteMode = getSiteMode();

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!navRef.current) return;
      if (!navRef.current.contains(event.target)) {
        setProductDropdownOpen(false);
        setResourcesDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    setProductDropdownOpen(false);
    setResourcesDropdownOpen(false);
    setMobileOpen(false);
    setMobileProductOpen(false);
    setMobileResourcesOpen(false);
  }, [location.pathname, location.hash]);

  return (
    <>
      <header className="landing-header">
        <div className="header-inner">
          <Link to="/" className="header-left">
            <img
              src={feraWordmark}
              alt="FeraBio"
              className="logo-wordmark"
            />
          </Link>

          <nav className="header-center" ref={navRef}>
            {isNavVisible("Home", siteMode) && (
              <Link
                to="/"
                className={`nav-tab ${location.pathname === "/" ? "active" : ""}`}
              >
                Home
              </Link>
            )}

            {isNavVisible("Product", siteMode) && (
              <div className="nav-dropdown">
                <button
                  className={`nav-tab nav-tab-dropdown ${location.pathname.startsWith("/platform") ||
                    location.pathname.startsWith("/product/")
                    ? "active"
                    : ""
                    }`}
                  type="button"
                  onClick={() => {
                    setProductDropdownOpen((open) => !open);
                    setResourcesDropdownOpen(false);
                  }}
                  aria-expanded={productDropdownOpen}
                  aria-haspopup="menu"
                >
                  Product
                  <span
                    className={`nav-chevron ${productDropdownOpen ? "open" : ""}`}
                    aria-hidden="true"
                  />
                </button>
                {productDropdownOpen && (
                  <div className="dropdown-menu" role="menu">
                    {PRODUCT_NAV_ITEMS.map((item) => (
                      <Link
                        key={item.route}
                        to={item.route}
                        className="dropdown-item"
                        onClick={() => setProductDropdownOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {isNavVisible("Resources", siteMode) && (
              <div className="nav-dropdown">
                <button
                  className={`nav-tab nav-tab-dropdown ${location.pathname.startsWith("/resources") ? "active" : ""
                    }`}
                  type="button"
                  onClick={() => {
                    setResourcesDropdownOpen((open) => !open);
                    setProductDropdownOpen(false);
                  }}
                  aria-expanded={resourcesDropdownOpen}
                  aria-haspopup="menu"
                >
                  Resources
                  <span
                    className={`nav-chevron ${resourcesDropdownOpen ? "open" : ""}`}
                    aria-hidden="true"
                  />
                </button>
                {resourcesDropdownOpen && (
                  <div className="dropdown-menu" role="menu">
                    {RESOURCES_NAV_ITEMS.map((item) => (
                      <Link
                        key={item.route}
                        to={item.route}
                        className="dropdown-item"
                        onClick={() => setResourcesDropdownOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {MAIN_NAV_ITEMS.filter((item) => item.route !== "/").map((item) =>
              isNavVisible(item.label, siteMode) ? (
                <Link
                  key={item.route}
                  to={item.route}
                  className={`nav-tab ${location.pathname === item.route ? "active" : ""}`}
                >
                  {item.label}
                </Link>
              ) : null
            )}
          </nav>

          <div className="header-right">
            <a className="btn-login" href={LOGIN_URL}>
              Log in
            </a>
            <Link
              to="/waitlist"
              className="btn-waitlist"
              onClick={() => setMobileOpen(false)}
            >
              Join Waitlist
            </Link>
            <button
              className="mobile-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle navigation"
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <nav className="mobile-nav">
          {isNavVisible("Home", siteMode) && (
            <Link
              to="/"
              className={`nav-tab ${location.pathname === "/" ? "active" : ""}`}
              onClick={() => setMobileOpen(false)}
            >
              Home
            </Link>
          )}

          {isNavVisible("Product", siteMode) && (
            <>
              <button
                className="nav-tab nav-tab-dropdown"
                onClick={() => setMobileProductOpen((open) => !open)}
                aria-expanded={mobileProductOpen}
              >
                Product
                <span
                  className={`nav-chevron ${mobileProductOpen ? "open" : ""}`}
                  aria-hidden="true"
                />
              </button>
              {mobileProductOpen && (
                <div className="mobile-submenu">
                  {PRODUCT_NAV_ITEMS.map((item) => (
                    <Link
                      key={item.route}
                      to={item.route}
                      className="nav-tab nav-subtab"
                      onClick={() => {
                        setMobileOpen(false);
                        setMobileProductOpen(false);
                      }}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}

          {isNavVisible("Resources", siteMode) && (
            <>
              <button
                className="nav-tab nav-tab-dropdown"
                onClick={() => setMobileResourcesOpen((open) => !open)}
                aria-expanded={mobileResourcesOpen}
              >
                Resources
                <span
                  className={`nav-chevron ${mobileResourcesOpen ? "open" : ""}`}
                  aria-hidden="true"
                />
              </button>
              {mobileResourcesOpen && (
                <div className="mobile-submenu">
                  {RESOURCES_NAV_ITEMS.map((item) => (
                    <Link
                      key={item.route}
                      to={item.route}
                      className="nav-tab nav-subtab"
                      onClick={() => {
                        setMobileOpen(false);
                        setMobileResourcesOpen(false);
                      }}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}

          {MAIN_NAV_ITEMS.filter((item) => item.route !== "/").map((item) =>
            isNavVisible(item.label, siteMode) ? (
              <Link
                key={item.route}
                to={item.route}
                className={`nav-tab ${location.pathname === item.route ? "active" : ""}`}
                onClick={() => setMobileOpen(false)}
              >
                {item.label}
              </Link>
            ) : null
          )}
        </nav>
      )}
    </>
  );
};

export default LandingHeader;
